/**
 * Working hours are CIVIL, not instants.
 *
 * "Mon 09:00–17:00" is a recurring rule, true every Monday, and it stays 09:00
 * across a DST transition. It is stored civil (`HH:mm` + `dayOfWeek`) against an
 * anchor zone — the tenant zone — and converted to UTC on the fly when the
 * engine needs a concrete day. That is the whole contract; see
 * `availability.service.ts`, which resolves each day's window in the tenant
 * zone before emitting UTC slot instants.
 *
 * Two consequences the UI has to respect, and which this module exists to make
 * hard to get wrong:
 *
 *  1. A civil rule has ONE correct primary rendering: its own anchor zone. It is
 *     the value the operator typed and the value the engine will use. Re-render
 *     it in the viewer's zone as the primary and you are showing a number that
 *     exists nowhere in the system.
 *
 *  2. Converting one to another zone is still USEFUL ("when is this tech
 *     actually reachable for me?"), but it is only well defined for a specific
 *     date, and it can cross midnight — Monday 09:00 New York is Monday 18:30 in
 *     Kolkata, but Monday 22:00 New York is TUESDAY 08:30. So a converted hint
 *     must carry its day shift, or it silently lies about which day the
 *     technician works.
 */
import { DateTime } from 'luxon';
import { safeTimeZone, timeZoneAbbrev } from '@/lib/date-values';

/**
 * `HH:mm` or `HH:mm:ss` → minutes since midnight; null when unparseable.
 *
 * The pattern is fully anchored on purpose. An unanchored prefix match accepted
 * junk like `09:00foo` and `09:00:99` as valid civil times and then formatted
 * and converted them as though they were, which for a shared helper just moves
 * the corruption downstream. `24` is likewise only legal as exactly `24:00` —
 * the end-of-day bound — never `24:30`.
 */
export function civilTimeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] === undefined ? 0 : Number(match[3]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return null;
  }
  if (hours < 0 || hours > 24) return null;
  if (minutes < 0 || minutes > 59) return null;
  if (seconds < 0 || seconds > 59) return null;
  // 24 is the end-of-day bound and nothing else.
  if (hours === 24 && (minutes !== 0 || seconds !== 0)) return null;
  return hours * 60 + minutes;
}

/** Minutes since midnight → a 12-hour civil label ("9:00 AM"). */
export function formatCivilTime(value: string | null | undefined): string {
  const minutes = civilTimeToMinutes(value);
  if (minutes === null) return value ?? '';
  // 24:00 is a legitimate end-of-day bound in the schema and is not a clock
  // reading — render it as such rather than wrapping to 12:00 AM.
  if (minutes === 1440) return 'midnight';
  const hours24 = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  const suffix = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${suffix}`;
}

export interface ConvertedCivilTime {
  /** The civil label in the target zone ("6:30 PM"). */
  label: string;
  /**
   * Whole days the reading moved: -1, 0 or +1. Non-zero means the technician's
   * Monday shift is read on a different weekday in the target zone.
   */
  dayShift: number;
  /**
   * Zone abbreviation at THIS reading's own instant ("EDT"/"EST").
   *
   * It has to come from the converted instant, not from the date: on a
   * transition day the two halves have different abbreviations, so a synthetic
   * stand-in instant can name the wrong one for a reading that sits on the
   * other side of the change.
   */
  zoneAbbrev: string;
}

/**
 * Convert a civil wall-clock time from one zone to another, ON a given date.
 *
 * A date is REQUIRED and is not optional sugar: the same civil rule maps to
 * different target readings either side of a DST transition, so there is no
 * date-free answer. Callers pass the week they are actually showing.
 */
export function convertCivilTime(
  value: string | null | undefined,
  fromZone: string,
  toZone: string,
  onDate: string,
): ConvertedCivilTime | null {
  const minutes = civilTimeToMinutes(value);
  const from = safeTimeZone(fromZone);
  const to = safeTimeZone(toZone);
  if (minutes === null || !from || !to) return null;

  const anchored = DateTime.fromISO(onDate, { zone: from });
  if (!anchored.isValid) return null;
  // WALL CLOCK, not elapsed time. `startOf('day').plus({ minutes })` looks
  // equivalent and is not: on a fall-back day midnight is still on the old
  // offset, so adding 540 minutes of elapsed time lands on 08:00, and every
  // row in a transition week reads an hour early. A civil rule means the time
  // on the clock, so set the clock.
  //
  // `24:00` is the end-of-day bound and has no hour-24 wall time — it is
  // midnight the following day, which is also what makes its day shift +1.
  const startOfDay = anchored.startOf('day');
  const source =
    minutes >= 1440
      ? startOfDay.plus({ days: 1 })
      : startOfDay.set({ hour: Math.floor(minutes / 60), minute: minutes % 60 });
  const target = source.setZone(to);
  if (!target.isValid) return null;

  // Compare CALENDAR dates, not the two zoned datetimes: diffing those measures
  // elapsed time, which is not a whole number of days once the zones differ, so
  // a genuine one-day shift rounds back to zero. Take the ISO dates and diff
  // those as plain dates.
  //
  // The base is `onDate` — the ROW's own day — not the post-addition source
  // date. For `24:00` those differ: adding 1440 minutes already rolls the source
  // into the next day, so diffing against it reported a shift of 0 and a
  // Monday 09:00–24:00 shift lost the marker saying it ends on Tuesday. The
  // question this answers is "relative to the day this row is about, where does
  // the reading land", and that base is `onDate` by definition.
  const targetDate = target.toISODate();
  if (!targetDate) return null;
  const dayShift =
    Math.round(
      DateTime.fromISO(targetDate, { zone: 'utc' }).diff(
        DateTime.fromISO(onDate, { zone: 'utc' }),
        'days',
      ).days,
    ) || 0;

  return {
    label: target.toFormat('h:mm a'),
    dayShift,
    zoneAbbrev: timeZoneAbbrev(to, target.toJSDate()),
  };
}

/** "+1 day" / "−1 day" / "" — the suffix that keeps a converted hint honest. */
export function formatDayShift(dayShift: number): string {
  if (dayShift === 0) return '';
  if (dayShift > 0) return ` (+${dayShift} day${dayShift === 1 ? '' : 's'})`;
  const magnitude = Math.abs(dayShift);
  return ` (−${magnitude} day${magnitude === 1 ? '' : 's'})`;
}

/**
 * A whole civil range converted for a hint line, e.g.
 * "6:30 PM – 2:30 AM (+1 day) IST". Returns null when the target zone is the
 * anchor zone (nothing to disambiguate) or anything fails to parse.
 */
export function formatConvertedCivilRange(
  startTime: string,
  endTime: string,
  fromZone: string,
  toZone: string,
  onDate: string,
): string | null {
  const start = convertCivilTime(startTime, fromZone, toZone, onDate);
  const end = convertCivilTime(endTime, fromZone, toZone, onDate);
  if (!start || !end) return null;
  // Each endpoint names the zone it actually lands in. The abbreviation used to
  // come from a synthetic UTC-noon instant on `onDate`, which on a transition
  // day can sit on the far side of the change from the reading it labels — a
  // 1:00 AM EDT reading was captioned EST.
  const abbrev = (part: ConvertedCivilTime) => (part.zoneAbbrev ? ` ${part.zoneAbbrev}` : '');
  // Both endpoints carry their OWN shift. Deriving the marker from the start
  // alone dropped it for the single most common case there is — 09:00–17:00 in
  // New York reads as 6:30 PM – 2:30 AM in Kolkata, where the START is same-day
  // and only the END crosses midnight. That printed an unmarked range implying
  // the shift ends the same evening, which is exactly the lie this helper
  // exists to prevent. When both endpoints move together the range moved as a
  // whole and one trailing marker says so; when they differ each endpoint is
  // marked where it actually lands.
  // Collapse to one trailing caption only when BOTH endpoints agree on the day
  // shift and the zone name; otherwise each is labelled where it lands. A range
  // straddling a transition genuinely has two abbreviations and saying so is
  // the honest rendering.
  if (start.dayShift === end.dayShift && start.zoneAbbrev === end.zoneAbbrev) {
    return `${start.label} – ${end.label}${formatDayShift(start.dayShift)}${abbrev(start)}`;
  }
  return `${start.label}${formatDayShift(start.dayShift)}${abbrev(start)} – ${end.label}${formatDayShift(
    end.dayShift,
  )}${abbrev(end)}`;
}

/**
 * The calendar date of `dayOfWeek` (0 = Sunday) within the week containing
 * `reference`, in `zone`.
 *
 * A weekly schedule row is about a WEEKDAY, but converting it needs a concrete
 * date — offsets differ across a DST transition, so feeding every row the same
 * date makes rows on the far side of the boundary an hour wrong.
 */
export function civilDateForWeekday(
  dayOfWeek: number,
  zone: string,
  reference: Date = new Date(),
): string | null {
  const safe = safeTimeZone(zone);
  if (!safe) return null;
  if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) return null;
  const today = DateTime.fromJSDate(reference, { zone: safe });
  if (!today.isValid) return null;
  // Walk back to the Sunday of the week CONTAINING `reference`, computed
  // directly rather than via `startOf('week')`. Luxon weeks start on Monday, so
  // for a Sunday reference `startOf('week')` jumps to the previous Monday and
  // the whole row set landed a week early — the Sunday row of 2026-11-01
  // resolved to 2026-10-25, which is exactly the DST-boundary week this helper
  // exists to get right.
  //
  // Luxon weekdays run 1 (Monday) – 7 (Sunday); `% 7` maps Sunday to 0 so the
  // reference day itself is the anchor when it IS a Sunday.
  const daysSinceSunday = today.weekday % 7;
  const sunday = today.startOf('day').minus({ days: daysSinceSunday });
  return sunday.plus({ days: dayOfWeek }).toISODate();
}
